/**
 * ledgerMinorUnits
 *
 * Turns a decimal amount into integer minor units of `currency`, taking the currency's digits from
 * `Intl.NumberFormat` (2 for USD, 0 for JPY). It shifts the ES shortest-round-trip decimal string
 * rather than multiplying, as `roundFormDeriveCents` does, so `1.005` USD is `101`, never `100`.
 * Rounds half away from zero, and returns `null` for a non-finite amount or a result that is not a
 * safe integer, since Postgres stores minor units as `bigint` read back as JS numbers.
 * Consumer: `evaluateLedgerAccrualBinding`'s `claimAmount` role.
 */

function shiftDecimal(text: string, shift: number): number {
    const [mantissa, exponent = '0'] = text.toLowerCase().split('e');
    return Number(`${mantissa}e${Number(exponent) + shift}`);
}

function ledgerMinorUnits(amount: number, currency: string): number | null {
    if (!Number.isFinite(amount)) return null;
    const digits = new Intl.NumberFormat('en-US', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
    if (digits === undefined) throw new Error(`ledgerMinorUnits: currency "${currency}" resolves no fraction digits`);
    const scaled = Math.round(shiftDecimal(String(Math.abs(amount)), digits));
    if (!Number.isSafeInteger(scaled)) return null;
    const minor = amount < 0 ? -scaled : scaled;
    return minor === 0 ? 0 : minor;
}

export { ledgerMinorUnits };
